import classNames from "classnames";
import styles from "./AscAddNewSchoolModal.module.scss";
import AscModal from "../../AscModal/AscModal";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscTextInput from "../../AscInputFields/AscTextInput/AscTextInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import AscButton from "../../AscButton/AscButton";
import { useState } from "react";

interface FormValues {
  schoolName: string;
  adminEmail: string;
  credits: number;
}

export interface AscAddNewSchoolModalProps {
  className?: string;
  visible: boolean;
  onClose: () => void;
  onSubmit: (value: FormValues) => Promise<void>;
}

export default function AscAddNewSchoolModal({
  className,
  visible,
  onClose,
  onSubmit,
}: AscAddNewSchoolModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formik = useFormik<Partial<FormValues>>({
    initialValues: {
      schoolName: "",
      adminEmail: "",
      credits: undefined,
    },
    validationSchema: Yup.object({
      schoolName: Yup.string().required("Required"),
      adminEmail: Yup.string()
        .email("Invalid email address")
        .required("Required"),
      credits: Yup.number()
        .required("Required")
        .min(0, "Number of credits can't be less than 0"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      await onSubmit(values as FormValues);
      setIsSubmitting(false);
    },
  });

  return (
    <AscModal
      visible={visible}
      withBackground={false}
      onClose={onClose}
      modalClassName={classNames(styles.ground, className)}
      innerContentClassName={styles.innerContent}
      header="Add New School"
    >
      <form onSubmit={formik.handleSubmit}>
        <AscHeading level={3}>Details</AscHeading>
        <div className={styles.inputGroup}>
          <label htmlFor="schoolName">Name of the school</label>
          <AscTextInput
            className={styles.textInput}
            type="text"
            id="schoolName"
            name="schoolName"
            placeholder="Name of the school"
            value={formik.values.schoolName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.schoolName && formik.touched.schoolName ? (
            <div className={styles.inputError}>{formik.errors.schoolName}</div>
          ) : null}
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="adminEmail">Admin E-Mail</label>
          <AscTextInput
            className={styles.textInput}
            type="email"
            id="adminEmail"
            name="adminEmail"
            value={formik.values.adminEmail}
            placeholder="E-mail of the Admin User for the school"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.adminEmail && formik.touched.adminEmail ? (
            <div className={styles.inputError}>{formik.errors.adminEmail}</div>
          ) : null}
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="credits">Number of credits</label>
          <AscTextInput
            className={styles.textInput}
            type={"number"}
            min={0}
            id="credits"
            name="credits"
            value={formik.values.credits}
            placeholder="Manually Assign Credits"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.credits && formik.touched.credits ? (
            <div className={styles.inputError}>{formik.errors.credits}</div>
          ) : null}
        </div>
        <AscButton
          loading={isSubmitting}
          type="submit"
          className={styles.submitButton}
        >
          Add
        </AscButton>
      </form>
    </AscModal>
  );
}
